/**
 * Curse effects, as far as a simulation is concerned: the window an effect is
 * live for, and what the two effects that touch the board do to it.
 *
 * `docs/DESIGN.md`, "Versus": "curses — attacks are effects, not garbage. FOG
 * hides the stack, MIRROR inverts left and right, RUSH multiplies gravity,
 * BLIND hides the next queue. Each is timed and shown as a countdown chip."
 *
 * ## Two effects change the simulation; two change only the screen
 *
 * RUSH (gravity) and MIRROR (left/right) are **inside the hash**: a gravity
 * boost moves the piece, and an inverted `left` is a different input from the
 * one that was pressed. Both are decided by `SimDriver`, from nothing but the
 * window and the tick, so a driver rebuilt from a snapshot mid-effect still
 * applies exactly the remaining half of it. FOG (hide the stack) and BLIND
 * (hide the next queue) touch nothing the engine knows about; they are read by
 * a renderer, off the same windows. All four kinds are held by the driver, so
 * that one list is what a resync, a resume or a checkpoint carries.
 *
 * Who picks effects, how long each lasts and when one may be issued are the
 * server's to decide and live with the wire contract (`@interlock/protocol`).
 */
import type { InputAction } from '@interlock/engine';

/** `docs/DESIGN.md`'s four curse effects. */
export type CurseEffect = 'fog' | 'mirror' | 'rush' | 'blind';

/** Spec order, and the order a loadout picker offers them in. */
export const CURSE_EFFECTS: readonly CurseEffect[] = ['fog', 'mirror', 'rush', 'blind'];

export function isCurseEffect(value: unknown): value is CurseEffect {
  return typeof value === 'string' && (CURSE_EFFECTS as readonly string[]).includes(value);
}

/**
 * RUSH's gravity multiplier while active, applied on top of whatever the
 * room's hardness and the seat's handicap already set. Two is "twice as fast"
 * — noticeable and genuinely threatening without being an instant,
 * unreactable slam; the same order of magnitude as the gap between two
 * adjacent hardness levels (0.7x, 1x, 1.5x, 2.2x). A whole number: it
 * multiplies integers that go into hashed state.
 */
export const CURSE_RUSH_GRAVITY_FACTOR = 2;

/**
 * One effect, live against one board, named by the ticks it starts and ends
 * on — never by a duration, for the same reason a `GarbageSchedule` names two
 * absolute ticks rather than a delay: both ends have to agree on *when*, and
 * an absolute tick is the only thing a resync, a resume or a checkpoint can
 * carry that survives being read back at a different wall-clock instant than
 * it was written.
 *
 * `startTick <= tick < endTick` is "active this tick", read fresh every tick
 * by whoever asks. Nothing here is mutated in place; a window that has fully
 * passed is simply one nobody's query matches any more.
 */
export interface CurseWindow {
  readonly effect: CurseEffect;
  readonly startTick: number;
  readonly endTick: number;
}

function isTick(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/**
 * Whether a value is a well-formed window: a known effect, and two whole,
 * non-negative ticks with the end after the start.
 *
 * `SimDriver.scheduleCurse` refuses anything else. A window's ticks decide
 * when hashed state changes, so a fractional or missing one must not get as
 * far as a board — whether it came off the wire or out of a checkpoint file.
 */
export function isCurseWindow(value: unknown): value is CurseWindow {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CurseWindow>;
  if (!isCurseEffect(candidate.effect)) return false;
  if (!isTick(candidate.startTick) || !isTick(candidate.endTick)) return false;
  return candidate.endTick > candidate.startTick;
}

/** Whether a window for `effect` is active at `tick` — the query a driver and a renderer both run. */
export function curseActiveAt(
  windows: readonly CurseWindow[],
  effect: CurseEffect,
  tick: number,
): boolean {
  return windows.some((w) => w.effect === effect && w.startTick <= tick && tick < w.endTick);
}

/**
 * MIRROR: the two move directions swap, and nothing else does — not rotation,
 * not soft or hard drop, not hold (`docs/prompts/07-specials.md`).
 */
export function invertLeftRight(action: InputAction): InputAction {
  if (action === 'left') return 'right';
  if (action === 'right') return 'left';
  return action;
}

/**
 * This tick's gravity modifier, in thousandths of the game's own speed: a
 * board's own ease (`basePermille`, 1000 for none), times RUSH's factor while
 * a RUSH window is open.
 *
 * The two multiply: a handicapped player under RUSH gets eased gravity that is
 * *also* doubled, which is correct — RUSH is what the opponent earned by
 * clearing, and a handicap is not a shield against it. Both factors are
 * integers, so the product is, and the engine multiplies it by the game's own
 * integer speed: 1500 (hard) x 925 x 2 is 2,775,000 units a tick, with
 * nothing rounded anywhere.
 */
export function gravityModifierAt(
  basePermille: number,
  windows: readonly CurseWindow[],
  tick: number,
): number {
  const rushActive = windows.length > 0 && curseActiveAt(windows, 'rush', tick);
  return basePermille * (rushActive ? CURSE_RUSH_GRAVITY_FACTOR : 1);
}
